"use client";

import { useCallback, useRef, useState } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";

// react-markdown's defaultUrlTransform strips data: URLs from <img src> as a
// security measure. We need data:image/* through for server-rendered charts
// (matplotlib stock charts via execute_stock_data_with_chart). Allow only
// image data URLs; everything else falls through to the default sanitizer.
const allowDataImages = (url: string) =>
  /^data:image\//i.test(url) ? url : defaultUrlTransform(url);

interface MarkdownRendererProps {
  content: string;
  className?: string;
  /**
   * Whether the stream producing `content` has finished. When true and an
   * `<antArtifact>` opening tag is found without a closing tag, the artifact is
   * marked `truncated` so the UI can show an error banner instead of a
   * permanent "generating..." placeholder.
   */
  isStreamComplete?: boolean;
  /**
   * When true, artifacts are uniformly scaled to fit the available container
   * space (preserving aspect ratio). Used by the desktop card-expand popup
   * where the response card grows to ~75vw × ~90vh and a chart laid out at
   * full container width would otherwise overflow vertically. In normal
   * (inline-in-card) rendering, artifacts continue to use their native
   * scrollHeight-up-to-800px sizing.
   */
  inPopup?: boolean;
}

// --- Artifact parsing and rendering ---

const ATTR_REGEX = /(\w+)="([^"]*)"/g;

type Segment =
  | { type: "text"; content: string }
  | { type: "artifact"; title: string; identifier: string; artifactType: string; content: string; truncated?: boolean };

function parseContentWithArtifacts(content: string, isStreamComplete: boolean = false): Segment[] {
  const segments: Segment[] = [];

  // Strip code fences wrapping artifact tags
  content = content.replace(/```(?:html|xml|svg|text)?\s*\n?(<antArtifact[\s\S]*?<\/antArtifact>)\s*\n?```/g, "$1");

  // Unescape HTML-encoded artifact tags
  if (content.includes("&lt;antArtifact")) {
    content = content
      .replace(/&lt;antArtifact/g, "<antArtifact")
      .replace(/&lt;\/antArtifact&gt;/g, "</antArtifact>")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, "&");
  }

  // Manual indexOf-based parsing (more robust than regex for large content)
  let pos = 0;
  const OPEN = "<antArtifact";
  const CLOSE = "</antArtifact>";

  while (pos < content.length) {
    const openIdx = content.indexOf(OPEN, pos);
    if (openIdx === -1) {
      // No more artifacts — rest is text
      const rest = content.slice(pos);
      if (rest.trim()) segments.push({ type: "text", content: rest });
      break;
    }

    // Text before the artifact
    if (openIdx > pos) {
      const before = content.slice(pos, openIdx);
      if (before.trim()) segments.push({ type: "text", content: before });
    }

    // Find the end of the opening tag (first > after the opening, respecting quotes)
    let tagEnd = -1;
    let inQuote = false;
    for (let i = openIdx + OPEN.length; i < content.length; i++) {
      if (content[i] === '"') inQuote = !inQuote;
      if (content[i] === '>' && !inQuote) { tagEnd = i; break; }
    }

    if (tagEnd === -1) {
      // Unclosed opening tag
      if (isStreamComplete) {
        // Stream finished but tag was cut off mid-attribute — model was
        // truncated before it could even finish the opening tag
        segments.push({ type: "text", content: "\n\n*Artifact incomplete — model output was truncated.*\n" });
      } else {
        // Mid-stream — hide and show placeholder
        segments.push({ type: "text", content: "\n\n*Generating artifact...*\n" });
      }
      break;
    }

    // Find the closing tag
    const closeIdx = content.indexOf(CLOSE, tagEnd);
    if (closeIdx === -1) {
      if (isStreamComplete) {
        // Stream finished but no closing tag — model was truncated after
        // starting the artifact. Render the partial content with a
        // truncation banner so the user can see what was generated.
        const openingTag = content.slice(openIdx, tagEnd + 1);
        const attrs: Record<string, string> = {};
        let attrMatch: RegExpExecArray | null;
        const attrRegex = new RegExp(ATTR_REGEX.source, "g");
        while ((attrMatch = attrRegex.exec(openingTag)) !== null) {
          attrs[attrMatch[1]] = attrMatch[2];
        }
        let partialContent = content.slice(tagEnd + 1);
        const trimmedPartial = partialContent.trim().toLowerCase();
        if (!trimmedPartial.startsWith("<!doctype") && !trimmedPartial.startsWith("<html")) {
          partialContent = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;padding:12px;font-family:-apple-system,system-ui,sans-serif;color:#222;}</style></head><body>${partialContent}</body></html>`;
        }
        segments.push({
          type: "artifact",
          title: attrs.title || "Artifact",
          identifier: attrs.identifier || "",
          artifactType: attrs.type || "text/html",
          content: partialContent,
          truncated: true,
        });
      } else {
        // Mid-stream — show placeholder
        segments.push({ type: "text", content: "\n\n*Generating artifact...*\n" });
      }
      break;
    }

    // Extract attributes from opening tag
    const openingTag = content.slice(openIdx, tagEnd + 1);
    const attrs: Record<string, string> = {};
    let attrMatch: RegExpExecArray | null;
    const attrRegex = new RegExp(ATTR_REGEX.source, "g");
    while ((attrMatch = attrRegex.exec(openingTag)) !== null) {
      attrs[attrMatch[1]] = attrMatch[2];
    }

    // Extract artifact content
    let artifactContent = content.slice(tagEnd + 1, closeIdx);
    const trimmed = artifactContent.trim().toLowerCase();
    if (!trimmed.startsWith("<!doctype") && !trimmed.startsWith("<html")) {
      artifactContent = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;padding:12px;font-family:-apple-system,system-ui,sans-serif;color:#222;}</style></head><body>${artifactContent}</body></html>`;
    }

    segments.push({
      type: "artifact",
      title: attrs.title || "Artifact",
      identifier: attrs.identifier || "",
      artifactType: attrs.type || "text/html",
      content: artifactContent,
    });

    pos = closeIdx + CLOSE.length;
  }

  return segments;
}

function ArtifactFrame({
  title,
  content,
  truncated = false,
  inPopup = false,
}: {
  title: string;
  content: string;
  truncated?: boolean;
  inPopup?: boolean;
}) {
  // Iframe height tracks the content's scrollHeight (measured on load).
  // In normal (in-card) mode it's capped at 800px to keep one card from
  // dominating the conversation. In popup mode there's no cap — the
  // iframe lives inside a 65%-of-card-width wrapper so the rendered
  // content stays a comfortable size on its own.
  const [naturalHeight, setNaturalHeight] = useState(400);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleLoad = useCallback(() => {
    try {
      const doc = iframeRef.current?.contentDocument;
      if (!doc) return;
      const h = doc.documentElement.scrollHeight;
      if (h > 100) {
        setNaturalHeight(inPopup ? h + 20 : Math.min(h + 20, 800));
      }
    } catch {
      /* cross-origin sandbox, ignore */
    }
  }, [inPopup]);

  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: "12px",
        overflow: "hidden",
        marginBottom: "12px",
        background: "var(--surface)",
      }}
    >
      <div
        style={{
          padding: "8px 12px",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "13px",
          color: "var(--text-secondary)",
        }}
      >
        <span>{title}</span>
        <button
          onClick={() => {
            const w = window.open();
            if (w) {
              w.document.write(content);
              w.document.close();
            }
          }}
          className="btn-ghost"
          style={{ fontSize: "12px", padding: "2px 8px" }}
        >
          Open in new tab
        </button>
      </div>
      {truncated && (
        <div
          style={{
            padding: "10px 12px",
            background: "rgba(230, 138, 0, 0.12)",
            borderBottom: "1px solid rgba(230, 138, 0, 0.3)",
            fontSize: "12px",
            color: "var(--text-secondary)",
            display: "flex",
            alignItems: "flex-start",
            gap: "8px",
          }}
        >
          <span style={{ color: "#e68a00", fontSize: "14px", lineHeight: 1 }}>⚠</span>
          <div>
            <strong style={{ color: "var(--text-primary)" }}>Artifact incomplete.</strong>{" "}
            The model ran out of output tokens before finishing this artifact. Increase Max Output Tokens in Settings → Parameters and try again.
          </div>
        </div>
      )}
      {inPopup ? (
        // Popup mode: render the iframe at 50% of the artifact card's
        // horizontal space, centered. Aspect ratio is preserved naturally
        // because the iframe's content (SVG with width:100% + height:auto,
        // or HTML body that follows viewport width) lays out within the
        // narrower box. Iframe height = measured scrollHeight × 1.2,
        // giving the artifact 20% extra vertical breathing room below
        // the rendered content.
        <div
          style={{
            width: "50%",
            margin: "0 auto",
          }}
        >
          <iframe
            ref={iframeRef}
            srcDoc={content}
            sandbox="allow-scripts"
            onLoad={handleLoad}
            style={{
              width: "100%",
              height: `${Math.round(naturalHeight * 1.2)}px`,
              border: "none",
              display: "block",
            }}
          />
        </div>
      ) : (
        <iframe
          ref={iframeRef}
          srcDoc={content}
          sandbox="allow-scripts"
          onLoad={handleLoad}
          style={{
            width: "100%",
            height: `${naturalHeight}px`,
            border: "none",
            background: "#ffffff",
          }}
        />
      )}
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      style={{
        background: "transparent",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-sm)",
        color: copied ? "var(--success)" : "var(--text-secondary)",
        cursor: "pointer",
        fontSize: "12px",
        padding: "2px 8px",
        transition: "all var(--transition-fast)",
      }}
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

const components: Components = {
  code({ className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || "");
    const codeString = String(children).replace(/\n$/, "");

    if (match) {
      return (
        <div
          style={{
            position: "relative",
            background: "#111111",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border)",
            marginBottom: "12px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "6px 12px",
              borderBottom: "1px solid var(--border)",
              fontSize: "12px",
              color: "var(--text-tertiary)",
            }}
          >
            <span>{match[1]}</span>
            <CopyButton text={codeString} />
          </div>
          <pre
            style={{
              margin: 0,
              padding: "12px",
              overflowX: "auto",
              fontSize: "13px",
              lineHeight: 1.5,
            }}
          >
            <code className={className} {...props}>
              {children}
            </code>
          </pre>
        </div>
      );
    }

    return (
      <code
        style={{
          background: "var(--surface-hover)",
          padding: "2px 6px",
          borderRadius: "4px",
          fontSize: "13px",
          fontFamily: "var(--font-mono)",
        }}
        {...props}
      >
        {children}
      </code>
    );
  },
  a({ href, children, ...props }) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  },
  // Neutralize GFM strikethrough (`~~text~~`). LLMs sometimes emit it as an
  // inline "self-correction" (strike a loose figure, follow with the precise
  // one), which renders as crossed-out text and reads like a glitch in a
  // finished answer. Drop the strike, keep the inner text. Single-tilde
  // strikethrough is disabled below so approximately-marked values survive.
  del({ children }) {
    return <>{children}</>;
  },
  table({ children, ...props }) {
    return (
      <div style={{ overflowX: "auto", marginBottom: "12px" }}>
        <table
          style={{
            borderCollapse: "collapse",
            width: "100%",
            fontSize: "13px",
          }}
          {...props}
        >
          {children}
        </table>
      </div>
    );
  },
  th({ children, ...props }) {
    return (
      <th
        style={{
          border: "1px solid var(--border)",
          padding: "8px 12px",
          textAlign: "left",
          background: "var(--surface-hover)",
          fontWeight: 600,
        }}
        {...props}
      >
        {children}
      </th>
    );
  },
  td({ children, ...props }) {
    return (
      <td
        style={{
          border: "1px solid var(--border)",
          padding: "8px 12px",
        }}
        {...props}
      >
        {children}
      </td>
    );
  },
};

export default function MarkdownRenderer({
  content,
  className,
  isStreamComplete = false,
  inPopup = false,
}: MarkdownRendererProps) {
  // Fast path: no artifacts, render as plain markdown
  if (!content.includes("<antArtifact") && !content.includes("&lt;antArtifact")) {
    return (
      <div className={`markdown-content ${className || ""}`}>
        <ReactMarkdown remarkPlugins={[[remarkGfm, { singleTilde: false }]]} components={components} urlTransform={allowDataImages}>
          {content}
        </ReactMarkdown>
      </div>
    );
  }

  const segments = parseContentWithArtifacts(content, isStreamComplete);

  return (
    <div className={`markdown-content ${className || ""}`}>
      {segments.map((segment, i) => {
        if (segment.type === "text") {
          return (
            <ReactMarkdown key={i} remarkPlugins={[[remarkGfm, { singleTilde: false }]]} components={components} urlTransform={allowDataImages}>
              {segment.content}
            </ReactMarkdown>
          );
        }
        return (
          <ArtifactFrame
            key={i}
            title={segment.title}
            content={segment.content}
            truncated={segment.truncated}
            inPopup={inPopup}
          />
        );
      })}
    </div>
  );
}
